import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  SlideoverDialog,
  SlideoverDialogButtons,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { IconRefresh } from "@tabler/icons-react";
import type { FC } from "react";
import { TeammateAgentSimulationCopilot } from "./teammateAgentSimulation.js";
import type { TeammateAgentConfigController } from "./useTeammateAgentConfigController.js";

export interface AgentSimulationSlideoverProps {
  controller: TeammateAgentConfigController;
}

/**
 * The "Try this agent" slideover: a live preview chat with the agent using the
 * latest draft config. Shared by the legacy Configuration tab and the static
 * agent layout.
 */
export const AgentSimulationSlideover: FC<AgentSimulationSlideoverProps> = ({
  controller,
}) => {
  const {
    teammateAgentId,
    isSimulationSlideoverOpen,
    setIsSimulationSlideoverOpen,
    simulationKey,
    shouldUseLiveToolCalls,
    setShouldUseLiveToolCalls,
    handleResetSimulation,
    isDeletingMessages,
  } = controller;

  return (
    <SlideoverContainer
      size={Size.SMALL}
      isDismissable
      isOpen={isSimulationSlideoverOpen}
      onOpenChange={(newIsOpen) => {
        if (!newIsOpen) {
          setIsSimulationSlideoverOpen(false);
          setShouldUseLiveToolCalls(false);
        }
      }}
    >
      <SlideoverDialog label="Try this agent">
        <SlideoverDialogTitleHeader isLoading={false}>
          <div className="flex w-full items-center justify-between">
            <div>
              <Label size={Size.MEDIUM}>Try this agent</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Chat with your agent to preview how it responds.
              </p>
            </div>
          </div>
        </SlideoverDialogTitleHeader>
        <SlideoverDialogContent>
          <div className="flex h-full flex-col overflow-hidden p-4">
            <TeammateAgentSimulationCopilot
              key={simulationKey}
              teammateAgentId={teammateAgentId}
              shouldUseLiveToolCalls={shouldUseLiveToolCalls}
            />
          </div>
        </SlideoverDialogContent>
        <SlideoverDialogButtons>
          <div className="flex flex-1 items-center">
            <Checkbox
              isChecked={shouldUseLiveToolCalls}
              onChange={setShouldUseLiveToolCalls}
              label="Use live tool calls"
            />
          </div>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={handleResetSimulation}
            isLoading={isDeletingMessages}
            isDisabled={isDeletingMessages}
            icon={<IconRefresh className="h-4 w-4" />}
          >
            Reset
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => setIsSimulationSlideoverOpen(false)}
          >
            Done
          </Button>
        </SlideoverDialogButtons>
      </SlideoverDialog>
    </SlideoverContainer>
  );
};
